/**
 * CRM Dashboard — Track 9
 *
 * Protected route: beforeLoad auth guard, SSR-safe.
 * Uses server functions only for CRM data.
 */

import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useAuth } from "../../../../components/AuthProvider";
import { createRouteError } from "../../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../../components/RouteSkeleton";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "../../../../components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { logger } from "../../../../lib/logger";
import { getUserMessage } from "../../../../lib/errors";
import {
  useCrmStatistics,
  useCrmClients,
  useCrmClientActivities,
  useCreateCrmClient,
  useDeleteCrmClient,
  useAddCrmClientActivity,
  type CrmClientRow,
  type CrmActivityRow,
} from "../../../../hooks/useCRM";

export const Route = createFileRoute("/_admin/integrations/crm/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return;
    const sb = context.supabase;
    if (!sb) return;
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("CRM"),
  component: CrmPage,
});

function CrmPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return (
    <CrmDashboard
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

const auth = (userId: string, accessToken: string) => ({
  userId,
  accessToken,
});

function CrmDashboard({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const a = auth(userId, accessToken);
  const { data: stats, isLoading: statsLoading } = useCrmStatistics(a);
  const { data: clientsData, isLoading: clientsLoading, refetch: refetchClients } = useCrmClients(a);
  const [selectedClient, setSelectedClient] = useState<CrmClientRow | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);

  const createClient = useCreateCrmClient(a);
  const deleteClient = useDeleteCrmClient(a);
  const addActivity = useAddCrmClientActivity(a);

  const { data: activitiesData } = useCrmClientActivities(
    a,
    selectedClient?.id ?? "",
    selectedClient ? 20 : 0
  );

  const clients: CrmClientRow[] = clientsData ?? [];
  const activities: CrmActivityRow[] = activitiesData ?? [];

  const handleCreateClient = async (formData: {
    name: string;
    email?: string;
    phone?: string;
    company?: string;
    status?: "lead" | "prospect" | "active" | "inactive";
    source?: string;
  }) => {
    try {
      await createClient.mutateAsync(formData);
      setShowCreateModal(false);
      refetchClients();
      toast.success("Client created successfully.");
    } catch (error) {
      logger.error("Failed to create CRM client", { error });
      toast.error(getUserMessage(error));
    }
  };

  const handleDeleteClient = (clientId: string) => {
    setDeletingClientId(clientId);
  };

  const handleAddActivity = async (formData: {
    type: "call" | "email" | "meeting" | "note" | "task";
    description?: string;
  }) => {
    if (!selectedClient) return;
    try {
      await addActivity.mutateAsync({ clientId: selectedClient.id, activity: formData });
      setShowActivityModal(false);
      toast.success("Activity added successfully.");
    } catch (error) {
      logger.error("Failed to add CRM activity", { error });
      toast.error(getUserMessage(error));
    }
  };

  return (
    <div>
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">CRM Dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage your clients and activities</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            Add Client
          </Button>
        </div>

        {/* Statistics */}
        {statsLoading ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading statistics...</p>
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Clients" value={stats.total_clients} />
            <StatCard label="Active Clients" value={stats.active_clients} />
            <StatCard label="Leads" value={stats.leads} />
            <StatCard label="Recent Activities" value={stats.recent_activities} />
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Clients List */}
          <div className="bg-card rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">Clients</h2>
            {clientsLoading ? (
              <p className="text-muted-foreground">Loading clients...</p>
            ) : clients.length === 0 ? (
              <p className="text-muted-foreground">No clients found. Add your first client!</p>
            ) : (
              <div className="space-y-2">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className={`p-3 rounded-md border cursor-pointer transition-colors ${
                      selectedClient?.id === client.id
                        ? "border-primary bg-accent"
                        : "hover:bg-accent"
                    }`}
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{client.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {client.company || client.email || "No details"}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        client.status === "active" ? "bg-green-100 text-green-800" :
                        client.status === "lead" ? "bg-blue-100 text-blue-800" :
                        client.status === "prospect" ? "bg-yellow-100 text-yellow-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {client.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Client Details / Activities */}
          <div className="bg-card rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-4">
              {selectedClient ? `${selectedClient.name} - Activities` : "Select a Client"}
            </h2>
            {selectedClient ? (
              <>
                <div className="mb-4 p-3 bg-accent rounded-md">
                  <p><strong>Email:</strong> {selectedClient.email || "N/A"}</p>
                  <p><strong>Phone:</strong> {selectedClient.phone || "N/A"}</p>
                  <p><strong>Company:</strong> {selectedClient.company || "N/A"}</p>
                  <p><strong>Source:</strong> {selectedClient.source || "N/A"}</p>
                </div>
                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    onClick={() => setShowActivityModal(true)}
                  >
                    Add Activity
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteClient(selectedClient.id)}
                  >
                    Delete
                  </Button>
                </div>
                {activities.length === 0 ? (
                  <p className="text-muted-foreground">No activities yet.</p>
                ) : (
                  <div className="space-y-2">
                    {activities.map((activity) => (
                      <div key={activity.id} className="p-2 border rounded-md">
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">{activity.type}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(activity.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {activity.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {activity.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">Select a client to view details and activities.</p>
            )}
          </div>
        </div>
      </div>

      {/* Create Client Modal */}
      {showCreateModal && (
        <ClientFormModal
          onSubmit={handleCreateClient}
          onClose={() => setShowCreateModal(false)}
          isLoading={createClient.isPending}
        />
      )}

      {/* Add Activity Modal */}
      {showActivityModal && selectedClient && (
        <ActivityFormModal
          onSubmit={handleAddActivity}
          onClose={() => setShowActivityModal(false)}
          isLoading={addActivity.isPending}
        />
      )}

      <AlertDialog open={deletingClientId !== null} onOpenChange={(open) => { if (!open) setDeletingClientId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The client record will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingClientId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!deletingClientId) return;
                try {
                  await deleteClient.mutateAsync(deletingClientId);
                  if (selectedClient?.id === deletingClientId) {
                    setSelectedClient(null);
                  }
                  refetchClients();
                  toast.success("Client deleted successfully.");
                } catch (error) {
                  logger.error("Failed to delete CRM client", { error });
                  toast.error(getUserMessage(error));
                } finally {
                  setDeletingClientId(null);
                }
              }}
            >
              Delete Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-card rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function ClientFormModal({
  onSubmit,
  onClose,
  isLoading,
}: {
  onSubmit: (data: {
    name: string;
    email?: string;
    phone?: string;
    company?: string;
    status?: "lead" | "prospect" | "active" | "inactive";
    source?: string;
  }) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<"lead" | "prospect" | "active" | "inactive">("lead");
  const [source, setSource] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, email, phone, company, status, source });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg border p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Add New Client</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="block text-sm font-medium mb-1">Name *</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <Label className="block text-sm font-medium mb-1">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label className="block text-sm font-medium mb-1">Phone</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div>
            <Label className="block text-sm font-medium mb-1">Company</Label>
            <Input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div>
            <Label className="block text-sm font-medium mb-1">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="block text-sm font-medium mb-1">Source</Label>
            <Input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g., Website, Referral"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ActivityFormModal({
  onSubmit,
  onClose,
  isLoading,
}: {
  onSubmit: (data: {
    type: "call" | "email" | "meeting" | "note" | "task";
    description?: string;
  }) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const [type, setType] = useState<"call" | "email" | "meeting" | "note" | "task">("note");
  const [description, setDescription] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ type, description });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg border p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Add Activity</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="block text-sm font-medium mb-1">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="task">Task</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="block text-sm font-medium mb-1">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Adding..." : "Add"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}